import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { findTheme, type ThemeId } from "@/lib/themes";

/**
 * The user's stored preference: a concrete registry theme, or 'system' —
 * a sentinel that resolves to light/dark LIVE from the OS appearance.
 * 'system' never enters the theme registry.
 */
export type ThemePreference = ThemeId | 'system';
type Theme = ThemePreference;

interface ThemeContextType {
  theme: Theme;
  /** The concrete theme applied to the document ('system' resolved to light/dark). */
  resolvedTheme: ThemeId;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function isValidPreference(value: string): value is ThemePreference {
  return value === 'system' || !!findTheme(value);
}

const DARK_MQ = '(prefers-color-scheme: dark)';

function subscribePrefersDark(cb: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mq = window.matchMedia(DARK_MQ);
  mq.addEventListener('change', cb);
  return () => mq.removeEventListener('change', cb);
}
const readPrefersDark = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(DARK_MQ).matches;

/** Whether the current path is a light-only marketing route (in-memory-only
 *  theme, no persistence). `/features` joins `/` because its illustrations,
 *  like the landing editor demo, use fixed light colors. */
function isLandingPath(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return path === '/' || path === '' || path === '/features';
}

export const ThemeProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'light';

    // Landing route is always light in-memory. Don't read localStorage so we
    // don't risk persisting it back. The persistence effect below also guards
    // by path, but skipping the read here keeps the landing render stable.
    if (isLandingPath()) return 'light';

    // Check if theme is saved in localStorage and validate against the
    // registry (or the 'system' sentinel).
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && isValidPreference(savedTheme)) {
      return savedTheme;
    }

    // No saved preference: follow the OS live (macOS parity — appearance
    // tracks the system until an explicit theme is chosen).
    return 'system';
  });

  // Live OS appearance. Only consulted while the preference is 'system'.
  const prefersDark = useSyncExternalStore(subscribePrefersDark, readPrefersDark, () => false);

  const resolvedTheme: ThemeId = theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme;

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  // Toggle between light and dark modes only (used by the free-user
  // ThemeToggle button). Premium themes get reset to light; 'system'
  // toggles away from its currently resolved scheme.
  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const scheme = prev === 'system'
        ? (readPrefersDark() ? 'dark' : 'light')
        : (findTheme(prev)?.scheme ?? 'light');
      return scheme === 'light' ? 'dark' : 'light';
    });
  }, []);

  // Apply theme to document when theme changes
  useEffect(() => {
    const root = window.document.documentElement;

    // Immediately remove all transition effects before changing theme
    root.classList.add('disable-transitions');

    // Set the theme attribute (drives CSS variables via [data-theme="..."]).
    // Always the RESOLVED theme — 'system' never reaches the DOM.
    root.dataset.theme = resolvedTheme;

    // Toggle the .dark class based on the theme's scheme. Tailwind's `dark:`
    // variants depend on this class (tailwind.config.ts: darkMode: ['class']).
    const scheme = findTheme(resolvedTheme)?.scheme ?? 'light';
    if (scheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Force a reflow to ensure transitions are disabled during class changes
    // eslint-disable-next-line no-void
    void root.offsetHeight;

    // Re-enable transitions after a very short delay
    const timer = setTimeout(() => {
      root.classList.remove('disable-transitions');
    }, 1);

    return () => clearTimeout(timer);
  }, [resolvedTheme]);

  // Persist theme to localStorage, but skip on the landing route. Visiting `/`
  // must not overwrite the user's saved preference (the landing render is
  // in-memory-only).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isLandingPath()) return;
    localStorage.setItem('theme', theme);
  }, [theme]);

  const value = useMemo(
    () => ({ theme, resolvedTheme, toggleTheme, setTheme }),
    [theme, resolvedTheme, toggleTheme, setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Read the user's saved theme PREFERENCE from localStorage ('system' kept
 * as-is). Use this when feeding the value back into `setTheme` so a 'system'
 * preference keeps following the OS instead of being pinned to a concrete id.
 */
export function readSavedThemePreference(): ThemePreference {
  if (typeof window === 'undefined') return 'light';
  try {
    const saved = localStorage.getItem('theme');
    if (saved && isValidPreference(saved)) return saved;
  } catch {
    // Safari private mode or other storage error — fall through.
  }
  return 'system';
}

// Custom hook to use the theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
